import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  Avatar,
  TextField,
  InputAdornment,
  Button,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  Pagination,
  CircularProgress,
  Tabs,
  Tab,
  Divider,
  useTheme,
  Alert
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Sort as SortIcon,
  SortByAlpha,
  PeopleAlt as PeopleIcon,
  ExitToApp as ExitToAppIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

// Types
interface Visitor {
  id: number;
  name: string;
  email: string;
  phone: string;
  company: string;
  purpose: string;
  host_name: string;
  host_id: number;
  check_in_time: string | null;
  check_out_time: string | null;
  status: 'pending' | 'checked-in' | 'checked-out';
  photo_url: string | null;
  badge_id: string;
  pre_registered: boolean;
  created_at: string;
}

// Status chip component
const StatusChip: React.FC<{ status: string }> = ({ status }) => {
  let color: 'success' | 'error' | 'warning' | 'default' = 'default';
  let label = status;
  
  switch (status) {
    case 'checked-in':
      color = 'success';
      label = 'Checked In';
      break;
    case 'checked-out':
      color = 'error';
      label = 'Checked Out';
      break;
    case 'pending':
      color = 'warning';
      label = 'Pending';
      break;
  }
  
  return (
    <Chip 
      label={label} 
      color={color} 
      size="small" 
      sx={{ 
        fontWeight: 500,
        borderRadius: '6px',
        textTransform: 'capitalize'
      }} 
    />
  );
};

// Empty state component
const EmptyState: React.FC<{ message: string; icon?: React.ReactNode }> = ({ message, icon }) => {
  return (
    <Box className="empty-state">
      {icon || <PeopleIcon fontSize="inherit" />}
      <Typography variant="h6" gutterBottom>No visitors found</Typography>
      <Typography variant="body2" color="text.secondary">{message}</Typography>
    </Box>
  );
};

const VisitorsList: React.FC = () => {
  const [visitors, setVisitors] = useState<Visitor[]>([]);
  const [filteredVisitors, setFilteredVisitors] = useState<Visitor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentTab, setCurrentTab] = useState(0);
  const [page, setPage] = useState(1);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedVisitor, setSelectedVisitor] = useState<Visitor | null>(null);
  const [sortAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
  
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const itemsPerPage = 8;
  
  // Check if user has permission to delete visitors
  const canDeleteVisitors = user?.role === 'Admin';
  
  // Get status from URL query params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const status = params.get('status');
    
    if (status) {
      switch (status) {
        case 'pending':
          setCurrentTab(1);
          break;
        case 'checked-in':
          setCurrentTab(2);
          break;
        case 'checked-out':
          setCurrentTab(3);
          break;
        default:
          setCurrentTab(0);
      }
    }
  }, [location.search]);
  
  // Fetch visitors data
  useEffect(() => {
    const fetchVisitors = async () => {
      try {
        setLoading(true);
        
        let endpoint = '/api/visitors';
        
        // If tab is not "All", filter by status
        if (currentTab !== 0) {
          const statuses = ['all', 'pending', 'checked-in', 'checked-out'];
          endpoint = `/api/visitors/status/${statuses[currentTab]}`;
        }
        
        const response = await api.get(endpoint);
        setVisitors(response.data);
        setFilteredVisitors(response.data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching visitors:', err);
        setError('Failed to load visitors. Please try again later.');
        setLoading(false);
      }
    };
    
    fetchVisitors();
  }, [currentTab]);
  
  // Filter visitors based on search query
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredVisitors(visitors);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = visitors.filter(visitor => 
        visitor.name.toLowerCase().includes(query) ||
        visitor.email.toLowerCase().includes(query) ||
        visitor.phone.toLowerCase().includes(query) ||
        visitor.company.toLowerCase().includes(query) ||
        visitor.badge_id.toLowerCase().includes(query) ||
        visitor.host_name.toLowerCase().includes(query)
      );
      setFilteredVisitors(filtered);
    }
    
    // Reset to first page when search changes
    setPage(1);
  }, [searchQuery, visitors]);
  
  // Handle tab change
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
    setPage(1);
  };
  
  // Handle menu open
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, visitor: Visitor) => {
    setAnchorEl(event.currentTarget);
    setSelectedVisitor(visitor);
  };
  
  // Handle menu close
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedVisitor(null);
  };
  
  // Handle sort menu open
  const handleSortMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setFilterAnchorEl(event.currentTarget);
  };
  
  // Handle sort menu close
  const handleSortMenuClose = () => {
    setFilterAnchorEl(null);
  };
  
  // Handle visitor view
  const handleViewVisitor = () => {
    if (selectedVisitor) {
      navigate(`/visitors/${selectedVisitor.id}`);
    }
    handleMenuClose();
  };
  
  // Handle visitor check-in
  const handleCheckIn = () => {
    if (selectedVisitor) {
      navigate(`/check-in?visitor=${selectedVisitor.id}`);
    }
    handleMenuClose();
  };
  
  // Handle visitor check-out
  const handleCheckOut = () => {
    if (selectedVisitor) {
      navigate(`/check-out/${selectedVisitor.id}`);
    }
    handleMenuClose();
  };
  
  // Handle visitor edit
  const handleEditVisitor = () => {
    if (selectedVisitor) {
      navigate(`/visitors/${selectedVisitor.id}/edit`);
    }
    handleMenuClose();
  };
  
  // Handle visitor delete
  const handleDeleteVisitor = async () => {
    if (!canDeleteVisitors) {
      alert('You do not have permission to delete visitors. Only Admin users can delete visitors.');
      handleMenuClose();
      return;
    }
    
    if (selectedVisitor) {
      if (window.confirm(`Are you sure you want to delete ${selectedVisitor.name}?`)) {
        try {
          await api.delete(`/api/visitors/${selectedVisitor.id}`);
          
          // Remove from state
          setVisitors(prev => prev.filter(v => v.id !== selectedVisitor.id));
          setFilteredVisitors(prev => prev.filter(v => v.id !== selectedVisitor.id));
        } catch (err) {
          console.error('Error deleting visitor:', err);
          alert('Failed to delete visitor. Please try again.');
        }
      }
    }
    handleMenuClose();
  };
  
  // Handle sort by name
  const handleSortByName = () => {
    const sorted = [...filteredVisitors].sort((a, b) => a.name.localeCompare(b.name));
    setFilteredVisitors(sorted);
    handleSortMenuClose();
  };
  
  // Handle sort by date
  const handleSortByDate = () => {
    const sorted = [...filteredVisitors].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    setFilteredVisitors(sorted);
    handleSortMenuClose();
  };
  
  // Calculate pagination
  const totalPages = Math.ceil(filteredVisitors.length / itemsPerPage);
  const paginatedVisitors = filteredVisitors.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{error}</Typography>
        <Button variant="contained" sx={{ mt: 2 }} onClick={() => window.location.reload()}>
          Retry
        </Button>
      </Box>
    );
  }
  
  return (
    <Box>
      {/* Page header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
          Visitors
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage and track all visitors in your organization.
        </Typography>
      </Box>
      
      {/* Search and filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search visitors..."
              variant="outlined"
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/pre-register')}
              sx={{ mr: 1 }}
            >
              Add Visitor
            </Button>
            <IconButton onClick={handleSortMenuOpen}>
              <SortIcon />
            </IconButton>
            <IconButton>
              <FilterIcon />
            </IconButton>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Tabs for filtering by status */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label="All Visitors" />
          <Tab label="Pending" />
          <Tab label="Checked In" />
          <Tab label="Checked Out" />
        </Tabs>
      </Paper>
      
      {/* Visitors grid */}
      {paginatedVisitors.length > 0 ? (
        <Grid container spacing={3}>
          {paginatedVisitors.map((visitor) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={visitor.id}>
              <Card 
                sx={{ 
                  height: '100%',
                  position: 'relative',
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                  },
                }}
              >
                <IconButton
                  sx={{ position: 'absolute', top: 8, right: 8 }}
                  onClick={(e) => handleMenuOpen(e, visitor)}
                >
                  <MoreVertIcon />
                </IconButton>
                
                <CardContent sx={{ pt: 3, pb: 3 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
                    <Avatar 
                      src={visitor.photo_url || undefined} 
                      sx={{ width: 64, height: 64, mb: 1, bgcolor: theme.palette.primary.main }}
                    >
                      {visitor.name.charAt(0)}
                    </Avatar>
                    <Typography variant="h6" align="center" noWrap sx={{ maxWidth: '100%' }}>
                      {visitor.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" align="center" noWrap sx={{ maxWidth: '100%' }}>
                      {visitor.company || 'No Company'}
                    </Typography>
                  </Box>
                  
                  <Divider sx={{ my: 1.5 }} />
                  
                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">Purpose:</Typography>
                      <Typography variant="body2" fontWeight="medium">{visitor.purpose}</Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">Host:</Typography>
                      <Typography variant="body2" fontWeight="medium">{visitor.host_name || 'Not Assigned'}</Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">Badge ID:</Typography>
                      <Typography variant="body2" fontWeight="medium">{visitor.badge_id}</Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">Date:</Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {format(new Date(visitor.created_at), 'MMM d, yyyy')}
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <StatusChip status={visitor.status} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Paper sx={{ p: 3 }}>
          <EmptyState 
            message={
              searchQuery
                ? "No visitors match your search criteria. Try adjusting your search."
                : "There are no visitors in this category yet."
            } 
          />
        </Paper>
      )}
      
      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination 
            count={totalPages} 
            page={page} 
            onChange={(_, value) => setPage(value)} 
            color="primary" 
            showFirstButton 
            showLastButton
          />
        </Box>
      )}
      
      {/* Visitor actions menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleViewVisitor}>
          <ListItemIcon>
            <ViewIcon fontSize="small" />
          </ListItemIcon>
          View Details
        </MenuItem>
        
        {selectedVisitor?.status === 'pending' && (
          <MenuItem onClick={handleCheckIn}>
            <ListItemIcon>
              <ExitToAppIcon fontSize="small" />
            </ListItemIcon>
            Check In
          </MenuItem>
        )}
        
        {selectedVisitor?.status === 'checked-in' && (
          <MenuItem onClick={handleCheckOut}>
            <ListItemIcon>
              <ExitToAppIcon fontSize="small" sx={{ transform: 'rotate(180deg)' }} />
            </ListItemIcon>
            Check Out
          </MenuItem>
        )}
        
        <MenuItem onClick={handleEditVisitor}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          Edit
        </MenuItem>
        
        {canDeleteVisitors && (
          <MenuItem onClick={handleDeleteVisitor}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <Typography color="error">Delete</Typography>
          </MenuItem>
        )}
      </Menu>
      
      {/* Sort menu */}
      <Menu
        anchorEl={sortAnchorEl}
        open={Boolean(sortAnchorEl)}
        onClose={handleSortMenuClose}
      >
        <MenuItem onClick={handleSortByName}>
          <ListItemIcon>
            <SortByAlpha fontSize="small" />
          </ListItemIcon>
          Sort by Name
        </MenuItem>
        
        <MenuItem onClick={handleSortByDate}>
          <ListItemIcon>
            <SortIcon fontSize="small" />
          </ListItemIcon>
          Sort by Date
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default VisitorsList;
